<script setup lang="ts">
import { ref, watch } from 'vue';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { browser } from '@dh-io-owpi/shared/src/environment';
import type { USPContentFragment } from '../utils/SpaHighlight';
import { useSlider } from '@/composables/useSlider';
import { vTransitionOverflow } from './vTransitionOverflow';
import { vHoverUsp } from './vHoverUsp';
import { ResponsiveImg } from '@dh-io-owpi/shared/src/components/ResponsiveImg';
import { WbIcon } from '@dh-io-owpi/shared/src/components/wb';
import { FootnoteReference } from '@dh-io-owpi/shared/src/components/ssr/footnotes';

defineProps<{
  items: USPContentFragment[];
}>();

const scrollableRef = ref<HTMLElement>();
const slider = useSlider(scrollableRef);
const { track } = useTrackingStore();
const imgLoading = ref('lazy');

if (browser) {
  const stopWatch = watch(
    () => slider.current,
    () => {
      imgLoading.value = 'eager';
      track({
        timeout: 300,
        category: 'feature',
        action: 'usp|highlights',
        label: 'see_more_cards',
      });
      stopWatch();
    },
  );
}
</script>

<template>
  <div class="owpi-spa-usp-modules" :data-item-count="items.length">
    <section ref="scrollableRef" class="owpi-spa-usp-modules__container">
      <article
        v-for="(item, index) in items"
        :key="item.slug"
        class="owpi-spa-usp-modules__item"
        v-hover-usp="{ index: index + 1, title: item.title }"
      >
        <button class="owpi-spa-usp-modules__item__img">
          <ResponsiveImg :picture="item.media.picture" :alt="item.media.altText" :loading="imgLoading" />
        </button>
        <div class="owpi-spa-usp-modules__item__text">
          <div class="wb-heading-s owpi-spa-usp-modules__item__text__title">
            <h3>{{ item.title }}</h3>
          </div>
          <div v-transition-overflow class="wb-text-l owpi-spa-usp-modules__item__text__description">
            <span>{{ item.description }}</span>
            <FootnoteReference
              v-if="item.footnote"
              class="-footnote"
              :content="item.footnote"
              :reference="`owpi_spa-usp-item-footnote_${index}`"
            />
          </div>
        </div>
      </article>
    </section>

    <div class="owpi-spa-usp-modules__navigation">
      <button
        v-if="!slider.atStart"
        class="wbx-round-button wbx-round-button--elevation-xs wbx-round-button--medium owpi-spa-usp-modules__navigation-button"
        tabindex="0"
        @click="slider.slideTo(-1)"
        aria-label="Previous page"
      >
        <WbIcon name="bds/chevron-left/24" flipRtl />
      </button>
      <div style="flex: 1; pointer-events: none" />
      <button
        v-if="!slider.atEnd"
        class="wbx-round-button wbx-round-button--elevation-xs wbx-round-button--medium owpi-spa-usp-modules__navigation-button"
        tabindex="0"
        @click="slider.slideTo(1)"
        aria-label="Next page"
      >
        <WbIcon name="bds/chevron-right/24" flipRtl />
      </button>
    </div>
    <wb7-dot-navigation
      class="owpi-spa-usp-modules__dot-navigation"
      data-allow-mismatch
      :pages="items.length"
      :current-page="slider.current + 1"
      prevent-interaction
    ></wb7-dot-navigation>
  </div>
</template>

<style lang="scss">
.owpi-spa-usp-modules {
  position: relative;

  &__container {
    --vertical-grid-gap: var(--wb-spacing-xs);
    gap: var(--vertical-grid-gap) var(--wb-grid-gutter-width);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; // firefox

    &:focus-visible {
      outline: var(--wb-color-shape-brand-default) dotted 1px;
    }

    &::-webkit-scrollbar {
      display: none; // safari + chrome
    }
  }

  &__item {
    background-color: var(--wb-black);
    color: var(--wb-white);
    position: relative;
    overflow: hidden;
    display: flex;
    height: 100%;
    text-align: start;

    &__img {
      aspect-ratio: 1 / 1;
      margin-bottom: calc(var(--wb-spacing-l) * 2);
      overflow: hidden;
      width: 100%;
      // remove button styles
      appearance: none;
      border: none;
      padding: 0;

      img {
        width: 100.1%; // fix visual bug
        height: 100%;
        object-fit: cover;
        will-change: transform;
        transition: transform 0.6s cubic-bezier(0.35, 0, 0, 1);
        transform-origin: bottom center;
      }
    }
    &__text {
      position: absolute;
      inset: auto 0 0 0;
      cursor: default;
      $spacing: var(--wb-spacing-l);
      padding: #{$spacing} var(--wb-spacing-s) var(--wb-spacing-xs) var(--wb-spacing-s);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0), var(--wb-black) #{$spacing}, var(--wb-black) 100%);

      &__title {
        text-wrap: balance;
        display: flex;
        align-items: end;
        --_title-height: 2;
        height: calc(var(--_title-height) * 1lh);
        > h3 {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: var(--_title-height);
          overflow: hidden;
        }
      }

      &__description {
        margin-top: var(--wb-spacing-xxs);
        min-height: 2lh;
        --_max-height: 5lh;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow-y: hidden; // Safari < 16.0
        overflow-y: clip;
        transition: max-height 0.6s cubic-bezier(0.35, 0, 0, 1);
        @media (prefers-reduced-motion: no-preference) {
          max-height: 2lh;
        }
      }
    }

    &:not(&.-active) .owpi-spa-usp-modules__item__text__description.-has-overflow.-overflow {
      -webkit-line-clamp: 2;
      position: relative;
      .-footnote {
        position: absolute;
        bottom: 0;
        right: 0;
        @supports (overflow: clip) {
          right: var(--_overflow-position, 0);
          translate: 100% 0;
        }
      }
    }

    &.-active {
      .owpi-spa-usp-modules__item__img img {
        @media (prefers-reduced-motion: no-preference) {
          transform: scale(107%);
        }
      }
      .owpi-spa-usp-modules__item__text__description {
        max-height: min(9lh, var(--_max-height));
        -webkit-line-clamp: 9;
      }
    }

    &:has(:focus-visible)::after {
      content: '';
      position: absolute;
      inset: 0;
      border: 2px solid var(--wb-color-border-brand-focus);
      border-inline-width: 1px;
    }
  }

  // navigation is hidden by default
  .owpi-spa-usp-modules__navigation,
  .owpi-spa-usp-modules__dot-navigation {
    display: none;
  }

  /// mobile
  @include wb.breakpoint-to(mq3) {
    &__container {
      display: flex;
      max-width: 100%;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-padding-inline-start: calc((100% - var(--wb-grid-width)) / 2);
      scroll-padding-inline-end: calc((100% - var(--wb-grid-width)) / 2);

      &::before,
      &::after {
        content: '';
        flex: 0 0 calc((100% - var(--wb-grid-width)) / 2 - var(--wb-grid-gutter-width));
      }
    }
    &__item {
      scroll-snap-align: center;
      flex-shrink: 0;
      width: calc(var(--wb-grid-width) - var(--wb-spacing-xs));
      max-width: 425px; // make sure we have 1 card until a iPhone Pro Max

      &:first-child {
        scroll-snap-align: start;
      }
      &:last-child {
        scroll-snap-align: end;
      }
    }

    .owpi-spa-usp-modules__dot-navigation {
      display: flex;
      margin-top: var(--wb-spacing-s);
      height: 10px;
      top: -10px;
    }

    @media (hover: hover) and (pointer: fine) {
      .owpi-spa-usp-modules__navigation {
        position: absolute;
        inset: calc(50% - 24px) 0 auto 0;
        display: flex;
        padding-inline: var(--wb-spacing-xs);
        z-index: 1;
      }
    }
  }

  /// desktop
  @include wb.breakpoint-from(mq4) {
    max-width: var(--wb-grid-width);
    margin-inline: auto;

    &__container {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
    }
    &__item {
      grid-column: span 2;
    }
    // make the first card bigger unless we have 3 cards
    &:not([data-item-count='3']) .owpi-spa-usp-modules__item:first-child,
    // make the last card bigger if we have 4 cards
    &[data-item-count='4'] .owpi-spa-usp-modules__item:last-child {
      grid-column: span 4;
      .owpi-spa-usp-modules__item__img {
        aspect-ratio: 16/9;
        margin-bottom: var(--wb-spacing-l);
      }
      .owpi-spa-usp-modules__item__text__title {
        --_title-height: 1;
      }
    }
  }
}
</style>
